<script setup lang="ts">
	import { usePaystack } from '@/composables/usePaystack'
	import type { ModalOption } from '@/components/Dialogs/options.vue'
	import useAccountApi from '~/services/apis/account'
	import useWalletApi from '~/services/apis/wallet'
	import useTransactionsApi from '~/services/apis/transactions'
	import { TimeOut } from '~/services/apis/timeout'

	export type PaymentResponse = { message: string; channel: PaymentGate } & PaymentCallbackResponse
	const prop = defineProps<{
		failSafeProps?: FailSafeMeta | null
		show: boolean
		event?: string
		metaData?: object
		eventCategory?: string
		withdrawAmount: number
		payStackOptions?: PayStackSchemas
		reason?: string
		beforePayment?: ((...rest: unknown) => void) | null
		afterPayment?: ((...rest: unknown) => void) | null
	}>()
	const { internationalNoFormat } = useCurrency()

	const { fetchWalletDetails } = useWalletApi()
	const { GenerateTransaction } = useTransactionsApi()

	const { User, getWalletDetails } = reactor(useUser())
	const { GetAccountInfo } = useAccountApi()

	const walletBalance = computed(() => (getWalletDetails.value?.balance as number) || 0)
	const paymentOptions = computed<ModalOption[]>(() => [
		{
			name: 'Pay from wallet',
			key: 'wallet',
			desc: `Balance: ₦${internationalNoFormat(walletBalance.value)}`,
			icon: {
				dir: 'wallet',
				name: 'wallet',
				wrapperClass: 'border-success-100 bg-success-50'
			},
			descClass: ['bg-white px-3 py-2 inline-flex rounded-xl border'],
			readonly: walletBalance.value < prop.withdrawAmount
		},
		{
			name: 'Pay Online',
			key: 'online',
			desc: 'Make secure online payments',
			icon: {
				dir: 'other',
				wrapperClass: 'border-success-100 bg-success-50',
				name: 'global'
			}
		}
	])

	const processingPayment = shallowRef(false)
	const channel = shallowRef('')

	const emit = defineEmits<{
		(event: 'update:show', parma: boolean): void
		(event: 'paymentComplete', param: PaymentResponse): void
	}>()
	const genTransaction = async (channel: TransactionContext['channel'], source: PaymentSource = '') => {
		const { data } = await GenerateTransaction({
			amount: Number(prop.withdrawAmount || 0),
			channel,
			name: prop?.reason || 'Transacted',
			source,
			metadata: JSON.stringify({
				event: prop.event ?? prop.failSafeProps?.event ?? '',
				event_category: prop.eventCategory ?? '',
				payload: prop.metaData ?? prop.failSafeProps?.body ?? [],
				user_id: User.value?.id ?? '',
				body: prop.failSafeProps?.body ?? {},
				endpoint: prop.failSafeProps?.endpoint ?? ''
			}),
			type: 'outflow',
			user_type: 'business'
		})
		return data.value?.data
	}
	const onPaymentComplete = (e: Partial<PaymentResponse>) => {
		processingPayment.value = false
		useNotification().alert({
			status: 200,
			message: 'Payment Made Successfully'
		})
		if (prop?.afterPayment) {
			prop.afterPayment()
		}
		emit('paymentComplete', e as PaymentResponse)
		emit('update:show', false)
	}
	const paymentChannel = usePaystack()
	const makePayment = async (e: ModalOption<'wallet' | 'online'>) => {
		processingPayment.value = true
		channel.value = e.key as string
		const channelMethod = e.key === 'online' ? 'paystack' : 'wallet'
		try {
			if (prop?.beforePayment) {
				prop.beforePayment()
			}
			const res = await genTransaction(channelMethod, channel.value === 'wallet' ? 'wallet' : 'online')
			if (res) {
				const transactionId = res.transaction.transaction_id
				if (channel.value === 'wallet') {
					if (walletBalance.value < prop.withdrawAmount) {
						processingPayment.value = false
						return useNotification().alert({
							status: 400,
							message: 'Your wallet balance is too low for this transaction'
						})
					}
					await TimeOut()
					onPaymentComplete({
						message: 'Payment successful',
						channel: channelMethod,
						redirecturl: '/',
						reference: '',
						status: '',
						trans: '',
						transaction: transactionId,
						trxref: ''
					})
				} else {
					const res = await paymentChannel?.initialize(
						prop?.payStackOptions || {
							email: User.value?.email || '',
							firstName: User.value?.first_name || '',
							lastName: User.value?.last_name || '',
							channels: ['card', 'bank', 'bank_transfer', 'ussd'],
							amount: Number(prop.withdrawAmount || 0),
							currency: 'NGN',
							quantity: 1,
							split: {},
							transactionCharge: 0,
							event: prop.event,
							event_category: prop.eventCategory,
							reference: transactionId
						}
					)
					onPaymentComplete({ ...res, channel: 'paystack', transaction: transactionId })
				}
			}
		} catch (err: unknown) {
			resolve(() =>
				useMixpanel()?.track('payment_biz_failed', {
					amount: prop.withdrawAmount
				})
			)
			useNotification().alert({
				status: 400,
				message: err?.message || 'Transaction Terminated'
			})
		} finally {
			processingPayment.value = false
		}
	}
	const onCancelPayment = () => {
		resolve(() => useMixpanel()?.track('payment_biz_cancelled', { amount: prop.withdrawAmount }))
		emit('update:show', false)
	}
	watch(
		() => prop.show,
		async isActive => {
			if (isActive) {
				await Promise.all([fetchWalletDetails(0), GetAccountInfo()])
			}
		}
	)
</script>

<template>
	<dialogs-options
		:show="show"
		:category-options="paymentOptions"
		title="Payment Method"
		paragraph="How would you like to make payment?"
		class="items-center"
		btn-testid="make-payment"
		:loading="processingPayment"
		@proceed="e => makePayment(e)"
		@cancel="onCancelPayment"
	/>
</template>
